import {
  Button, Flex, Text
} from '@huspy/briks-web';
import { CaseCreationBankFormsRoute } from '@modules/opportunities/presentation/routes';
import useClientOpportunity from '@modules/opportunities/entities/opportunity/query/useClientOpportunity';
import { useTranslation } from 'react-i18next';
import {
  useContext, useEffect, useState
} from 'react';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import { CaseCreationContext } from '@modules/opportunities/presentation/pages/CaseCreation/index';
import useGetDigitalBankApplications from '@modules/opportunities/entities/bankApplication/query/useGetDigitalBankApplications';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import { BankApplicationDocument } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import { MORTGAGE_USAGE } from '@modules/opportunities/entities/opportunity/const';
import { BankSection } from './components/BankSection';

const getSchema = (mortgageUsage?: string) =>
  z.object({
    documents:
      mortgageUsage === MORTGAGE_USAGE.COMMERCIAL
        ? z.array(z.array(z.object({})))
        : z.array(z.array(z.object({})).min(1)),
  });

const CaseCreationBankForms = () => {
  const { opportunityId } = CaseCreationBankFormsRoute.useParams();
  const { data: opportunityData, isLoading: isLoadingOpportunity } = useClientOpportunity(opportunityId);
  const {
    data: digitalBankApplication,
    isLoading: isLoadingDigitalBankApplication,
  } = useGetDigitalBankApplications(
    opportunityId
  );
  const [wasDirty, setWasDirty] = useState(false);
  const { t } = useTranslation();
  const { goToNextState } = useContext(CaseCreationContext);

  const documents = opportunityData?.opportunity.bank_applications.map((bankApplication) => {
    if (bankApplication.documents.length > 0) {
      return bankApplication.documents;
    }
    if (bankApplication.status !== BANK_APPLICATION_STATUS.draft) {
      return [{ file_name: '' }] as BankApplicationDocument[];
    }
    return [];
  });

  const schema = getSchema(opportunityData?.opportunity.mortgage_usage);
  const form = useForm({
    initialValues: { documents },
    validate: zodResolver(schema),
  });

  const onSubmit = () => {
    goToNextState();
  };

  useEffect(() => {
    form.setValues({ documents });

    if (wasDirty) {
      form.validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityData]);

  useEffect(() => {
    if (form.isDirty()) {
      setWasDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values]);

  if (isLoadingOpportunity
    || !opportunityData
    || isLoadingDigitalBankApplication
  ) {
    return <></>;
  }

  const hasDigitalBankApplication = (bankId: string) => {
    const digitalApps = digitalBankApplication?.digital_bank_applications;
    if (!digitalApps) return false;

    return digitalApps.some((item) => item.bank_external_id === bankId);
  };

  const getDigitalBankApplicationLinks = (bankId: string) => {
    const digitalApps = digitalBankApplication?.digital_bank_applications;
    if (!digitalApps) return null;

    const existedDigitalApp = digitalApps.find((item) => item.bank_external_id === bankId);

    if (!existedDigitalApp) return null;

    return {
      admin_link: existedDigitalApp.document?.links?.admin_link ?? null,
      main_link: existedDigitalApp.document?.links?.main_link ?? (existedDigitalApp.document?.link ?? null),
      main_signer_link: existedDigitalApp.document?.links?.main_signer_link ?? null,
      co_link: existedDigitalApp.document?.links?.co_link ?? null,
      co_signer_link: existedDigitalApp.document?.links?.co_signer_link ?? null,
    };
  };

  const getDigitalBankApplicationStatus = (bankId: string) => {
    const digitalApps = digitalBankApplication?.digital_bank_applications;
    if (!digitalApps) return null;

    const existedDigitalApp = digitalApps.find((item) => item.bank_external_id === bankId);

    if (!existedDigitalApp) return null;

    if (!existedDigitalApp.document?.status) return null;

    return existedDigitalApp.document.status;
  };

  const getDigitalBankApplicationId = (bankId: string) => {
    const digitalApps = digitalBankApplication?.digital_bank_applications;
    if (!digitalApps) return null;

    const existedDigitalApp = digitalApps.find((item) => item.bank_external_id === bankId);

    if (!existedDigitalApp) return null;

    if (!existedDigitalApp.document?.external_id) return null;

    return existedDigitalApp.document.external_id;
  };

  return (
    <form onSubmit={ form.onSubmit(onSubmit) }>
      <Flex mb='4' gap='2' direction='column'>
        <Text size='2xl'>{t('opportunity.caseSubmission.bankForms.title')}</Text>
        <Text size='md' color='text-secondary'>{t('opportunity.caseSubmission.bankForms.subtitle')}</Text>
      </Flex>
      <Flex gap='spacing-4' direction='column'>
        { opportunityData.opportunity.bank_applications.map((bankApplication, index) => (
          <BankSection
            key={ bankApplication.bank_application_external_id }
            bankApplication={ bankApplication }
            opportunityId={ opportunityId }
            clientId={ opportunityData.opportunity.client_external_id }
            hasErrors={ form.getInputProps(`documents.${index}`).error }
            applicants={ opportunityData.opportunity.applicants }
            digitalBankApplicationId={ getDigitalBankApplicationId(bankApplication.bank_details.external_id) }
            hasDigitalBankApplication={ hasDigitalBankApplication(bankApplication.bank_details.external_id) }
            digitalBankApplicationLinks={ getDigitalBankApplicationLinks(bankApplication.bank_details.external_id) }
            digitalBankApplicationStatus={ getDigitalBankApplicationStatus(bankApplication.bank_details.external_id) }
            isCommercial={ opportunityData.opportunity.mortgage_usage === MORTGAGE_USAGE.COMMERCIAL }
          />
        ))}
      </Flex>
      <Flex justify='flex-end' gap='2' mt='4'>
        <Button type='submit' data-testid='proceed-btn'>
          {t('opportunity.caseSubmission.bankForms.buttons.proceed')}
        </Button>
      </Flex>
    </form>
  );
};

export default CaseCreationBankForms;
